import { Component, ElementRef, OnInit, SecurityContext, ChangeDetectionStrategy } from '@angular/core';
import { Test } from '../_model/test';
import { APIService } from '../_service/api.service';
import { HelperService } from '../_service/helper.service';

import { ChartService } from '../_service/chart.service';
import { CategoryScale, Chart, LinearScale, LineController, LineElement, PointElement, Tooltip } from 'chart.js';

Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Tooltip);

import { TranslateService } from '@ngx-translate/core';

import { PDFService } from '../_service/pdf.service';

import { DomSanitizer } from '@angular/platform-browser';

@Component({
    selector: 'history',
    templateUrl: 'history.component.html',
    styleUrls: ['history.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HistoryComponent implements OnInit {

    // Graph
    canvas: any;
    ctx: any;
    chart: Chart;

    dateValue: any;

    chartColors: any = {
        red: 'rgb(255, 99, 132)',
        orange: 'rgb(255, 159, 64)',
        yellow: 'rgb(255, 205, 86)',
        green: 'rgb(80, 250, 100)',
        blue: 'rgb(52, 152, 219)',
        purple: 'rgb(117, 54, 230)',
        grey: 'rgb(201, 203, 207)',
        white: 'rgb(255, 255, 255)',
    };

    // User
    currentUser: any;
    didLoad: boolean;

    // averages
    downloadArray: any[];
    uploadArray: any[];
    pingArray: any[];
    downloadAverage: number;
    uploadAverage: number;
    pingAverage: number;
    numberOfTests: number;
    ispSet: any[];
    selectedIspId: any;
    ispFilterArray: any;
    dateFilterArray: any;

    // Table
    data: any[] = [];
    rows: any[] = [];
    selectedRows: any[] = [];
    reorderable = true;

    public isCollapsed = true;

    downloadSpeed = '';
    uploadSpeed = '';

    translations: any = {};

    tests: Test[];

    constructor(private api: APIService,
                private helper: HelperService,
                private chartService: ChartService,
                private pdfService: PDFService,
                private elementRef: ElementRef,
                translate: TranslateService,
                private sanitizer: DomSanitizer) {
        translate.get('history.trend.downloadspeed').subscribe((res: string) => {
            this.downloadSpeed = res;
            this.translations['downloadSpeed'] = res;
        });
        translate.get('history.trend.uploadspeed').subscribe((res: string) => {
            this.uploadSpeed = res;
            this.translations['uploadSpeed'] = res;
        });
    }

    reset() {
        this.dateValue = null;
        this.runFilter();
    }

    onInputChanged(event: any) {
        this.runFilter();
    }

    createIspSelectionObject(array: any[]) {
        const resultArray: any[] = [];
        for (let i = 1; i <= array.length; ++i) {
            const value = array[i];
            const object = {};
            object['id'] = i;
            object['value'] = value;
            resultArray.push(object);
        }
        return resultArray;
    }

    loadTests() {
        if (this.currentUser) {
            const token: string = this.currentUser.token;
            this.api.getTests(token)
                .subscribe(
                    data => {
                        this.data = data;
                        this.presentData(data.reverse());
                        this.tests = data
                    },
                    error => {
                        console.log(error);
                    });
        } else {
            const tests: Test[] = this.helper.getTestLocalStorage();
            if (tests) {
                this.data = tests;
                this.presentData(tests);
                this.tests = tests
            }
        }
    }

    presentData(data: any[]) {
        this.ispSet = this.createIspSelectionObject(this.data.map(a => a.isp.isp).filter(this.onlyUnique)).filter(a => a.value != undefined);
        this.rows = data;
        this.rows = this.rows.filter(a => a.download < 500);
        this.rows = this.rows.filter(a => new Date(a.testDate) > new Date('2017-01-01T02:14:17.782Z'));
        this.downloadArray = this.rows.map(a => a.download);
        this.uploadArray = this.rows.map(a => a.upload);
        this.pingArray = this.rows.map(a => (Math.round(a.ping)));
        this.didLoad = true;

        this.numberOfTests = this.rows.length;
        this.downloadAverage = this.arrayAverage(this.downloadArray);
        this.uploadAverage = this.arrayAverage(this.uploadArray);
        this.pingAverage = this.pingArrayAverage(this.pingArray);

        setTimeout(() => {
            this.drawCharts();
        }, 100);
    }

    ispSelectOnChange(e: any) {
        this.ispFilterArray = e;
        this.runFilter();
    }

    runFilter() {
        let filteredData = this.data;
        if (this.ispFilterArray && this.ispFilterArray.length > 0) {
            filteredData = filteredData.filter(a => (this.ispFilterArray.map(a => a.value)).indexOf(a.isp.isp) >= 0);
        }
        if (this.dateValue && this.dateValue.length > 0) {
            filteredData = filteredData.filter(a => new Date(a.testDate) >= this.dateValue[0] && new Date(a.testDate) <= this.dateValue[1]);
        }
        this.presentData(filteredData);
    }

    selectAllUsers(): void {
        // this.selectedUsers = [...users];
    }

    ngOnInit() {
        this.currentUser = JSON.parse(localStorage.getItem('currentUser'));
        this.loadTests();
    }

    formatDate(dateString: Date) {
        const options: Intl.DateTimeFormatOptions = {
            weekday: 'short', year: 'numeric', month: 'short',
            day: 'numeric', hour: '2-digit', minute: '2-digit',
        };

        // return date.toLocaleDateString("en-US")
        return dateString.toLocaleTimeString('en-us', options);
    }

    drawCharts() {
        const labels = this.rows.map(a => this.formatDate(new Date(a.testDate)));

        // this.translate.get('latency.testing').subscribe((res: string) => {
        //     this.testType = res;
        // });

        const lineChartData = {
            // labels: ["January", "February", "March", "April", "May", "June", "July"],
            labels: labels,
            datasets: [{
                label: this.downloadSpeed,
                borderColor: this.chartColors.blue,
                backgroundColor: 'rgba(52, 152, 219, 0.6)',
                fill: false,
                data: this.downloadArray,
                yAxisID: 'y-axis-1',
                borderWidth: 6.0,
                pointRadius: 6.0,
                pointBackgroundColor: this.chartColors.white,
                pointBorderWidth: 4.0,
                pointHoverRadius: 6.0,
                pointHoverBorderWidth: 4.0,
                tension: 0.4,
                pointHoverBackgroundColor: this.chartColors.blue,
            }, {
                label: this.uploadSpeed,
                borderColor: this.chartColors.purple,
                backgroundColor: 'rgba(117, 54, 230, 0.6)',
                fill: false,
                data: this.uploadArray,
                yAxisID: 'y-axis-2',
                borderWidth: 6.0,
                pointRadius: 6.0,
                pointBackgroundColor: this.chartColors.white,
                pointBorderWidth: 4.0,
                pointHoverRadius: 6.0,
                pointHoverBorderWidth: 4.0,
                tension: 0.4,
                pointHoverBackgroundColor: this.chartColors.purple,
            }],
        };

        const options = {
            responsive: true,
            plugins: {
                title: {
                    display: false,
                    text: 'Chart.js Line Chart - Multi Axis',
                },
                tooltip: {
                    callbacks: {
                        label: (item) => `${item.parsed.y} Mbps`,
                    },
                },
                legend: {
                    display: false,
                },
            },
            scales: {
                x: {
                    display: false,
                    ticks: {
                        autoSkip: true,
                    },
                },
                'y-axis-1': {
                    type: 'linear',
                    display: true,
                    position: 'left',
                    beginAtZero: true,
                    title: {
                        display: true,
                        text: this.downloadSpeed + ' [Mbps]',
                        color: this.chartColors.blue,
                        font: { size: 15, weight: 'bold' },
                    },
                    grid: {
                        drawOnChartArea: true, // only want the grid lines for one axis to show up
                        // chart.js 2 drew a darker zero line
                        color: (context) => context.tick?.value === 0 ? 'rgba(0, 0, 0, 0.25)' : 'rgba(0, 0, 0, 0.1)',
                    },
                    ticks: {
                        font: { weight: 'bold' },
                    },
                },
                'y-axis-2': {
                    type: 'linear',
                    display: true,
                    position: 'right',
                    beginAtZero: true,
                    title: {
                        display: true,
                        text: this.uploadSpeed + ' [Mbps]',
                        color: this.chartColors.purple,
                        font: { size: 15, weight: 'bold' },
                    },
                    grid: {
                        drawOnChartArea: false, // only want the grid lines for one axis to show up
                    },
                    ticks: {
                        font: { weight: 'bold' },
                    },
                },
            },
        } as const;

        this.canvas = document.getElementById('canvas');
        this.ctx = this.canvas.getContext('2d');

        // chart.js 3+ refuses to reuse a canvas; drawCharts() runs again on every filter change
        this.chart?.destroy();
        this.chart = new Chart(this.ctx, {
            type: 'line',
            data: lineChartData,
            options: options,
        });
    }

    randomScalingFactor() {
        return (Math.random() > 0.5 ? 1.0 : -1.0) * Math.round(Math.random() * 100);
    }

    ngAfterViewInit() {

    }

    arrayAverage(array: any[]): number {
        let total = 0;
        for (let i = array.length - 1; i >= 0; i--) {
            const val = parseFloat(array[i]);
            total = total + val;
        }
        const result = (Math.round(total / array.length * 100) / 100);
        if (!Number.isNaN(result)) {
            return result;
        } else {
            return 0;
        }
    }

    pingArrayAverage(array: any[]): number {
        let total = 0;
        for (let i = array.length - 1; i >= 0; i--) {
            const val = Math.round(array[i]);
            total = total + val;
        }
        const result = (Math.round(total / array.length));
        if (!Number.isNaN(result)) {
            return result;
        } else {
            return 0;
        }
    }

    onlyUnique(value, index, self) {
        return self.indexOf(value) === index;
    }

    collapsed(event: any): void {
    }

    expanded(event: any): void {
    }

    // Data table delete rows
    delete(row: any) {
        // console.log(row)
        // If logged in delete from database
        if (this.currentUser) {
            const token: string = this.currentUser.token;
            this.api.delete(token, row['id'])
                .subscribe(
                    data => {
                        // console.log(data)
                        this.deleteLocalTest(row['id']);

                    },
                    error => {
                        // console.log(error)
                    });
        } else {
            this.deleteLocalTest(row['id']);
        }
    }

    getTestLocalStorage(): Test[] {
        const localStorageItem = JSON.parse(localStorage.getItem('tests'));
        return localStorageItem == null ? [] : localStorageItem.tests;
    }

    private setTestLocalStorage(tests: Test[]) {
        localStorage.setItem('tests', JSON.stringify({tests: tests}));
    }

    deleteLocalTest(testId: number) {
        let tests: Test[] = this.helper.getTestLocalStorage();
        if (tests.length > 0) {
            tests = tests.filter(test => test.id !== testId);
            this.helper.setTestLocalStorage(tests);
        }
        this.loadTests();
    }

    loadPDF() {
        this.pdfService.createPDF(this.tests).subscribe(data => {
            const blob = new Blob([data.body], {type: 'application/pdf'});

            var link = document.createElement('a');
            link.href = this.sanitizer.sanitize(SecurityContext.RESOURCE_URL, this.sanitizer.bypassSecurityTrustResourceUrl(window.URL.createObjectURL(blob)));
            link.download = "Speedcheck - " + (new Date().toLocaleString("en-US")) + ".pdf"
            link.click();
        },
        error => console.log(error));
    }

}
